/**
 * Componente para proteger rotas que requerem permissões específicas
 */

import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";

interface ProtectedRouteProps {
  children: ReactNode;
  requiredPermission?: string;
  requiredPermissions?: string[];
  requireAll?: boolean;
  fallbackPath?: string; // path to redirect when lacking permission/auth
}

export function ProtectedRoute({
  children,
  requiredPermission,
  requiredPermissions,
  requireAll = false,
  fallbackPath = "/forbidden",
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const { hasPermission, hasAnyPermission, hasAllPermissions, isAdmin } =
    usePermissions();

  // Aguardar carregamento da autenticação
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Verificar se está autenticado
  if (!isAuthenticated) {
    // Redireciona para página inicial (Login é exibido na Index)
    return <Navigate to="/" replace />;
  }

  // Admin sempre tem acesso
  if (isAdmin) {
    return <>{children}</>;
  }

  // Verificar permissão única
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to={fallbackPath} replace state={{ requiredPermission }} />;
  }

  // Verificar múltiplas permissões
  if (requiredPermissions && requiredPermissions.length > 0) {
    const hasAccess = requireAll
      ? hasAllPermissions(requiredPermissions)
      : hasAnyPermission(requiredPermissions);

    if (!hasAccess) {
      return (
        <Navigate
          to={fallbackPath}
          replace
          state={{ requiredPermissions, requireAll }}
        />
      );
    }
  }

  return <>{children}</>;
}

/**
 * HOC para proteger componentes que requerem permissões
 */
export function withPermission(
  Component: React.ComponentType<any>,
  requiredPermission: string
) {
  return function ProtectedComponent(props: any) {
    return (
      <ProtectedRoute requiredPermission={requiredPermission}>
        <Component {...props} />
      </ProtectedRoute>
    );
  };
}

/**
 * HOC para proteger componentes que requerem múltiplas permissões
 */
export function withPermissions(
  Component: React.ComponentType<any>,
  requiredPermissions: string[],
  requireAll = false
) {
  return function ProtectedComponent(props: any) {
    return (
      <ProtectedRoute
        requiredPermissions={requiredPermissions}
        requireAll={requireAll}
      >
        <Component {...props} />
      </ProtectedRoute>
    );
  };
}
